import { BookOpen, ClipboardList, Flag, PenLine, type LucideIcon } from 'lucide-react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * QUICK ACTION ROWS, AND WHO IS OFFERED WHICH HALF
 *
 * Separated from the card that draws them so the rule can be tested against
 * the real role presets. What matters here is not how a row looks but whether
 * it survives — a list naming a permission its intended audience does not hold
 * renders as an empty card, and an empty card is invisible in exactly the way
 * that makes it ship.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface QuickLink {
  to: string;
  requires: string;
  /** Spoken to a screen reader and shown on hover. */
  label: string;
}

export interface QuickAction {
  label: string;
  /**
   * One line under the label on the tile, and the `title` on hover.
   *
   * Keep it to about THIRTY characters. The tiles are four across a column on
   * the manager dashboard and the line is clamped to one — "Build one, or
   * assign to your team" and "Objections raised on your questions" both
   * truncated mid-word, which reads as a rendering fault rather than as a
   * summary. The admin panel truncates it too.
   */
  hint: string;
  icon: LucideIcon;
  /** Open the list. */
  view: QuickLink;
  /** Start a new one. Absent where the product has no such screen. */
  create?: QuickLink;
  /**
   * Which waiting-count to badge this tile with, if any.
   *
   * A name rather than a number, because the list is a constant and the
   * figures arrive per request — the tile says which queue it is about and the
   * dashboard supplies the count.
   */
  pending?: 'evaluations' | 'questionReports';
}

/** A row after gating: either half may have been taken away. */
export interface GatedQuickAction extends Omit<QuickAction, 'view' | 'create'> {
  view?: QuickLink;
  create?: QuickLink;
}

/**
 * Each half is gated on its own permission.
 *
 * Somebody who may read the course list but not add to it gets the eye and no
 * plus — rather than a row that offers a button the API would refuse, or no row
 * at all when half of it was perfectly usable. A row with neither half left is
 * dropped, and a list with no rows left draws no card.
 */
export function gateActions(actions: QuickAction[], permissions: string[]): GatedQuickAction[] {
  const held = new Set(permissions);

  return actions
    .map((action) => ({
      ...action,
      view: held.has(action.view.requires) ? action.view : undefined,
      create: action.create && held.has(action.create.requires) ? action.create : undefined,
    }))
    .filter((action) => action.view || action.create);
}

/**
 * ── A MANAGER'S FOUR ───────────────────────────────────────────────────────
 *
 * A Manager's dashboard is the learner's page — their own courses, their own
 * deadlines — because they are an Employee plus governance, not a smaller
 * Admin. That left the management half of their job reachable only from the
 * sidebar, off the page they actually land on.
 *
 * These are the Content section's entries, minus Categories: one shared
 * org-wide list they hold `category.view` but not `category.manage` for, so
 * the screen opens with every button disabled. It is off their menu now too —
 * see `navigation.ts`.
 */
/**
 * ── WHAT EACH TILE CARRIES BESIDES ITS NAME ────────────────────────────────
 *
 * Courses and Assessments get a plus; the two queues get a count of what is
 * waiting. Both sit on a row of their own ABOVE the label rather than beside
 * it — see `ManagerQuickActions`.
 *
 * Laid out in a line they did not fit, which is why the plus was dropped once
 * already: a quarter of this column is about 165px, and the icon, the label,
 * the padding and a button want 180, so "Assessments" ran out of its box and
 * over the plus. Stacking is what makes room for both without the label
 * losing.
 *
 * The counts are the same figures their screens show, counted the same way —
 * their own team, on their own papers. A badge reading 17 that opens a queue
 * of 3 is worse than no badge.
 */
export const MANAGER_QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Courses',
    hint: 'Build or assign to your team',
    icon: BookOpen,
    view: { to: '/admin/courses', requires: 'course.view', label: 'View all courses' },
    create: { to: '/admin/courses?new=1', requires: 'course.create', label: 'Create a course' },
  },
  {
    label: 'Assessments',
    hint: 'Questions, marking and limits',
    icon: ClipboardList,
    view: { to: '/admin/assessments', requires: 'assessment.view', label: 'View all assessments' },
    create: {
      to: '/admin/assessments?new=1',
      requires: 'assessment.create',
      label: 'Create an assessment',
    },
  },
  /**
   * Nothing in either queue is started by a manager anyway. Both fill when
   * somebody sits a paper they wrote, and both are scoped to their own team on
   * their own assessments — see `evaluation.service.ts`.
   */
  {
    label: 'Evaluation Queue',
    hint: 'Answers waiting to be marked',
    icon: PenLine,
    view: {
      to: '/admin/evaluation',
      requires: 'evaluation.view_queue',
      label: 'Open the evaluation queue',
    },
    pending: 'evaluations',
  },
  {
    label: 'Question Reports',
    hint: 'Objections on your questions',
    icon: Flag,
    view: {
      to: '/admin/question-reports',
      requires: 'rebuttal.view',
      label: 'Review question reports',
    },
    pending: 'questionReports',
  },
];
